import { UseFormRegister, FieldValues, Path } from 'react-hook-form';
import { OptionalDescriptionTooltip } from 'features/entry-form-v2/component/OptionalDescriptionTooltip';
import { Asterisk } from 'components/UI/Asterisk';
import { InputError } from 'components/UI/Inputs/InputError';
import { LanguageType } from 'types/index';
import { useLanguage } from 'features/hooks/useLanguage';

interface CodelistRadioGroupProps<TFieldValues extends FieldValues> {
	register: UseFormRegister<TFieldValues>;
	fieldName: Path<TFieldValues>;
	disabled?: boolean;
	error?: string;
}

export function CodelistRadioGroup<TFieldValues extends FieldValues>({
	register,
	fieldName,
	disabled = false,
	error
}: CodelistRadioGroupProps<TFieldValues>) {
	const language = useLanguage();
	const translation = CODELIST_DICTIONARY[language].codelist;

	return (
		<fieldset
			className="flex flex-col"
			role="radiogroup"
			disabled={disabled}
			aria-label={translation.label}
		>
			<legend className="text-sm font-semibold mb-2 flex gap-2">
				<div>
					{translation.label}
					<Asterisk />
				</div>

				<OptionalDescriptionTooltip description={translation.description} />
			</legend>

			<div className="flex flex-row gap-4">
				<label className="flex gap-1 text-sm">
					{translation.yes}
					<input type="radio" value="true" {...register(fieldName)} />
				</label>

				<label className="flex gap-1 text-sm">
					{translation.no}
					<input type="radio" value="false" {...register(fieldName)} />
				</label>
			</div>

			{error && <InputError error={error} />}
		</fieldset>
	);
}

export const CODELIST_DICTIONARY = {
	[LanguageType.English]: {
		codelist: {
			label: 'Allow personal identifiable information',
			description:
				'If you want to store personal identifiable information in the project, you must store them in the codelist (and not as variables). To get access to the codelist in the project, you enable it here.',
			yes: 'Yes',
			no: 'No'
		}
	},
	[LanguageType.Norwegian]: {
		codelist: {
			label: 'Tillat personlig identifiserbar data',
			description:
				'Dersom du skal lagre personlig identifiserbar informasjon i prosjektet, må disse lagres i kodelisten (og ikke som variabler). For å få tilgang til kodeliste i prosjektet aktiverer du her.',
			yes: 'Ja',
			no: 'Nei'
		}
	}
};
